import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { CASE_STUDIES, CONTACT } from '../data';
import FormattedText from '../components/FormattedText';
import { fetchCaseStudyBySlug } from '../lib/contentful/services/caseStudies';
import './DetailPage.css';

function splitParagraphs(text = '') {
  return text
    .split(/\n{2,}/)
    .map(chunk => chunk.trim())
    .filter(Boolean);
}

export default function CaseStudyDetail() {
  const { slug } = useParams();
  const localCaseStudy = useMemo(() => CASE_STUDIES.find(item => item.id === slug), [slug]);
  const [caseStudy, setCaseStudy] = useState(localCaseStudy || null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let isActive = true;

    async function loadCaseStudy() {
      setLoading(true);
      setError('');

      try {
        const contentfulCaseStudy = await fetchCaseStudyBySlug(slug);
        if (!isActive) return;

        if (contentfulCaseStudy) {
          setCaseStudy(contentfulCaseStudy);
          return;
        }

        if (localCaseStudy) {
          setCaseStudy(localCaseStudy);
          return;
        }

        setCaseStudy(null);
      } catch (err) {
        if (!isActive) return;
        setError(err instanceof Error ? err.message : 'Failed to load case study.');
        setCaseStudy(localCaseStudy || null);
      } finally {
        if (isActive) setLoading(false);
      }
    }

    loadCaseStudy();

    return () => {
      isActive = false;
    };
  }, [slug, localCaseStudy]);

  if (loading && !caseStudy) {
    return <div className="detail-404">Loading case study...</div>;
  }
  if (!caseStudy) return <div className="detail-404">Case study not found.</div>;

  const cs = caseStudy;
  const articleParagraphs = splitParagraphs(cs.detail?.article);
  const resultsField = cs.detail?.results;
  const resultsList = Array.isArray(resultsField) ? resultsField : (resultsField?.items || []);
  const resultsText = typeof resultsField === 'string' ? resultsField : (resultsField?.text || '');
  const resultParagraphs = splitParagraphs(resultsText);

  return (
    <main className="detail-page">
      <div className="detail-hero detail-hero--dark">
        <div className="container">
          <Link to="/#case-studies" className="detail-back detail-back--light">← Back to Case Studies</Link>
          <h1 className="detail-title detail-title--light">{cs.title}</h1>
          <FormattedText as="p" className="detail-hero-summary" text={cs.summary} />
        </div>
      </div>

      <div className="detail-body container">
        {error ? <p className="detail-load-warning">{error} Showing local fallback content.</p> : null}

        <div className="cs-meta">
          <div className="cs-meta-item">
            <span className="cs-meta-label">Duration</span>
            <span className="cs-meta-value">{cs.detail?.duration || 'N/A'}</span>
          </div>
          <div className="cs-meta-item">
            <span className="cs-meta-label">Tools Used</span>
            <div className="cs-tools">
              {(cs.detail?.tools || []).length ? (
                cs.detail.tools.map((tool, i) => (
                  <span key={i} className="cs-tool-tag">{tool}</span>
                ))
              ) : (
                <span className="cs-meta-value">N/A</span>
              )}
            </div>
          </div>
        </div>

        <div className="detail-content">
          <div className="cs-section">
            <h2 className="detail-section-title">The Challenge</h2>
            <FormattedText as="p" className="detail-text" text={cs.detail?.challenge || 'Not provided.'} />
          </div>

          <div className="cs-section">
            <h2 className="detail-section-title">The Solution</h2>
            <FormattedText as="p" className="detail-text" text={cs.detail?.solution || 'Not provided.'} />
          </div>

          <div className="cs-section">
            <h2 className="detail-section-title">Results</h2>
            {resultsList.length ? (
              <ul className="detail-list">
                {resultsList.map((result, i) => (
                  <li key={i} className="detail-list-item">
                    <span className="detail-list-mark detail-list-mark--gold">◆</span>
                    <FormattedText as="span" text={result} />
                  </li>
                ))}
              </ul>
            ) : resultParagraphs.length ? (
              resultParagraphs.map((paragraph, i) => (
                <FormattedText key={i} as="p" className="detail-text detail-article-text" text={paragraph} />
              ))
            ) : (
              <p className="detail-text">Not provided.</p>
            )}
          </div>

          {articleParagraphs.length ? (
            <div className="cs-section">
              <h2 className="detail-section-title">Article</h2>
              {articleParagraphs.map((paragraph, i) => (
                <FormattedText key={i} as="p" className="detail-text detail-article-text" text={paragraph} />
              ))}
            </div>
          ) : null}

          <div className="detail-cta">
            <a href={`mailto:${CONTACT.email}`} className="btn-primary">
              Start a Similar Project
            </a>
          </div>
        </div>
      </div>
    </main>
  );
}
